import React, { useState, useEffect, useCallback } from 'react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { makeApiCall } from '../utils/customerUtils'

interface BoardStatus {
  id: number
  name: string
  color: string
  position: number
  board_id?: number | null
  is_closed?: boolean
  is_pending_compliance?: boolean
  is_archived?: boolean
  is_submitted?: boolean
}

interface BoardStatusPanelProps {
  boardId: number | null
  boardName: string
  isOpen: boolean
  onClose: () => void
  onStatusesChanged?: () => void
}

const COLORS = [
  '#EF4444', '#F97316', '#EAB308', '#22C55E',
  '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899',
  '#10B981', '#6B7280',
]

const BoardStatusPanel: React.FC<BoardStatusPanelProps> = ({
  boardId,
  boardName,
  isOpen,
  onClose,
  onStatusesChanged,
}) => {
  const [statuses, setStatuses] = useState<BoardStatus[]>([])
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState('#3B82F6')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')
  const [error, setError] = useState<string | null>(null)

  const fetchStatuses = useCallback(async () => {
    if (!boardId) return
    setLoading(true)
    try {
      const res = await makeApiCall(`api/deals/statuses?board_id=${boardId}`)
      if (res.ok) {
        const data = await res.json()
        setStatuses((data.statuses ?? data).sort((a: BoardStatus, b: BoardStatus) => a.position - b.position))
      }
    } finally {
      setLoading(false)
    }
  }, [boardId])

  useEffect(() => {
    if (isOpen && boardId) fetchStatuses()
  }, [isOpen, boardId, fetchStatuses])

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return
    const items = Array.from(statuses)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    setStatuses(items)

    await makeApiCall(`api/deals/statuses/${moved.id}/move`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position: result.destination.index }),
    })
    onStatusesChanged?.()
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim() || !boardId) return
    const res = await makeApiCall('api/deals/statuses', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName.trim(), color: newColor, board_id: boardId }),
    })
    if (res.ok) {
      setNewName('')
      setNewColor('#3B82F6')
      setAdding(false)
      await fetchStatuses()
      onStatusesChanged?.()
    } else {
      const err = await res.json()
      setError(err.error || 'Failed to create status')
    }
  }

  const handleSaveEdit = async (id: number) => {
    const res = await makeApiCall(`api/deals/statuses/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editName.trim(), color: editColor }),
    })
    if (res.ok) {
      setEditingId(null)
      await fetchStatuses()
      onStatusesChanged?.()
    } else {
      const err = await res.json()
      setError(err.error || 'Failed to update status')
    }
  }

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete status "${name}"? Move all deals out of it first.`)) return
    const res = await makeApiCall(`api/deals/statuses/${id}`, { method: 'DELETE' })
    if (res.ok) {
      await fetchStatuses()
      onStatusesChanged?.()
    } else {
      const err = await res.json()
      setError(err.error || 'Failed to delete status')
    }
  }

  const toggleFlag = async (id: number, flag: 'closed' | 'pending_compliance' | 'archived', current: boolean) => {
    await makeApiCall(`api/deals/statuses/${id}/flags`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flag_type: flag, enabled: !current }),
    })
    await fetchStatuses()
    onStatusesChanged?.()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-900 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Board Statuses</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{boardName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-4 mt-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-700 dark:text-red-400 flex justify-between">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Status list */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 dark:border-white" />
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="statuses">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                    {statuses.map((status, index) => (
                      <Draggable key={status.id} draggableId={String(status.id)} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 transition-shadow ${
                              snapshot.isDragging ? 'shadow-lg' : ''
                            }`}
                          >
                            {editingId === status.id ? (
                              /* Inline edit form */
                              <div className="p-3 space-y-2">
                                <input
                                  type="text"
                                  value={editName}
                                  onChange={e => setEditName(e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
                                  autoFocus
                                />
                                <div className="flex flex-wrap gap-1.5">
                                  {COLORS.map(c => (
                                    <button
                                      key={c}
                                      type="button"
                                      onClick={() => setEditColor(c)}
                                      className={`w-5 h-5 rounded-full transition-transform ${
                                        editColor === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : ''
                                      }`}
                                      style={{ backgroundColor: c }}
                                    />
                                  ))}
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleSaveEdit(status.id)}
                                    className="px-3 py-1 text-xs font-medium bg-gray-900 text-white dark:bg-white dark:text-gray-900 rounded-lg hover:opacity-90"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingId(null)}
                                    className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="p-3">
                                <div className="flex items-center space-x-2">
                                  {/* Drag handle */}
                                  <span
                                    {...provided.dragHandleProps}
                                    className="text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing"
                                  >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                                    </svg>
                                  </span>

                                  {/* Color swatch */}
                                  <span
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: status.color }}
                                  />

                                  <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {status.name}
                                  </span>

                                  {/* Edit / Delete */}
                                  <button
                                    onClick={() => {
                                      setEditingId(status.id)
                                      setEditName(status.name)
                                      setEditColor(status.color)
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleDelete(status.id, status.name)}
                                    className="p-1 text-gray-400 hover:text-red-500 rounded"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>

                                {/* Flag badges */}
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {[
                                    { key: 'is_closed',              label: 'Closed',      flag: 'closed' as const,              color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
                                    { key: 'is_pending_compliance',  label: 'Compliance',  flag: 'pending_compliance' as const,  color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' },
                                    { key: 'is_archived',            label: 'Archive',     flag: 'archived' as const,            color: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' },
                                  ].map(({ key, label, flag, color }) => {
                                    const active = !!(status as any)[key]
                                    return (
                                      <button
                                        key={key}
                                        onClick={() => toggleFlag(status.id, flag, active)}
                                        className={`px-2 py-0.5 text-xs rounded-full border transition-opacity ${
                                          active
                                            ? `${color} border-transparent`
                                            : 'bg-transparent border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-600 opacity-50 hover:opacity-100'
                                        }`}
                                      >
                                        {label}
                                      </button>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>

        {/* Add status form */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-4">
          {adding ? (
            <form onSubmit={handleAdd} className="space-y-2">
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Status name"
                required
                autoFocus
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
              />
              <div className="flex flex-wrap gap-1.5">
                {COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColor(c)}
                    className={`w-5 h-5 rounded-full transition-transform ${
                      newColor === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : ''
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="px-3 py-1.5 text-xs font-medium bg-gray-900 text-white dark:bg-white dark:text-gray-900 rounded-lg hover:opacity-90"
                >
                  Add Status
                </button>
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Status
            </button>
          )}
        </div>
      </div>
    </>
  )
}

export default BoardStatusPanel
